import {Map, fromJS} from 'immutable';
import dieselReducer from '../../decorators/DieselReducer';
import {adaptCatalogItem} from '../../utils/CatalogUtils';
import {CatalogItem} from '../../models/CatalogItem';
import {
    CatalogTypes,
    CatalogAction,
    CatalogItemPayload,
    BulkCatalogItemsPayload,
    TimestampPayload,
    CountryInfoPayload
} from '../actions/catalog.actions';
import {CatalogId} from '../../commonTypes';

export type CatalogItemsMap = Map<CatalogId, CatalogItem>;

interface CatalogStateProps {
    catalogItems: CatalogItemsMap;
    latestCatalogTimestamp: string | undefined;
    latestCatalogAcked: string | undefined;
    defaultCurrency: string | undefined;
}

export type CatalogState = Map<keyof CatalogStateProps, CatalogStateProps[keyof CatalogStateProps]>;

export const DEFAULT_STATE: CatalogState = Map({
    catalogItems: Map(),
    latestCatalogTimestamp: undefined,
    latestCatalogAcked: undefined,
    defaultCurrency: undefined
}) as CatalogState;

const bulkUpdateCatalogItems = (state: CatalogState, items?: CatalogItem[]) => {
    if (!items || !items.length) return state;
    return state.update('catalogItems', (catalogItems: unknown) =>
        items.reduce(
            (acc: CatalogItemsMap, x: CatalogItem) => acc.set(x.id as string, x),
            catalogItems as CatalogItemsMap
        )
    );
};

export const catalogReducer = (
    state: CatalogState = DEFAULT_STATE,
    {type, payload}: CatalogAction
) => {
    switch (type) {
        case CatalogTypes.CATALOG_FETCH_ITEM_SUCCESS: {
            const {catalogItem} = payload as CatalogItemPayload;
            return bulkUpdateCatalogItems(state, [adaptCatalogItem(catalogItem)]);
        }

        case CatalogTypes.CATALOG_BULK_FETCH_ITEMS_SUCCESS: {
            const {catalogItems} = payload as BulkCatalogItemsPayload;
            return bulkUpdateCatalogItems(state, catalogItems.map(x => adaptCatalogItem(x)));
        }

        case CatalogTypes.GET_LATEST_CATALOG_UPDATE_SUCCESS: {
            const {timestamp} = payload as TimestampPayload;
            return state.set('latestCatalogTimestamp', timestamp);
        }

        case CatalogTypes.ACK_CATALOG_UPDATE: {
            const {timestamp} = payload as TimestampPayload;
            return state.set('latestCatalogAcked', timestamp);
        }

        case CatalogTypes.GET_COUNTRY_INFO_COMPLETE: {
            const {countryInfo} = payload as CountryInfoPayload;
            return state.set('defaultCurrency', countryInfo.defaultCurrency);
        }

        default:
            return state;
    }
};

function clientBootstrap(serverState) {
    return Object.keys(serverState).reduce((acc: CatalogState, key: string) => {
        if (key === 'catalogItems') {
            return acc.set(key, Map(serverState[key]));
        }
        return acc.set(key as keyof CatalogStateProps, fromJS(serverState[key]));
    }, DEFAULT_STATE);
}

export default dieselReducer('catalog', catalogReducer, null, clientBootstrap);
